import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { CTA } from '../components/CTA';
import { Dashboard } from '../components/Dashboard';
import { Features } from '../components/Features';
import { HowItWorks } from '../components/HowItWorks';
import { PageShell } from '../components/PageShell';
import { Problem } from '../components/Problem';
import { Testimonials } from '../components/Testimonials';
import { products } from '../data/products';
import { easeEnter, stagger } from '../utils/motion';

/**
 * The features page. Deliberately un-accented paper and ink: it belongs to all
 * three products, so giving it one product's signature colour would misfile it.
 */
export function PlatformPage() {
  const reduceMotion = useReducedMotion();
  return <PageShell data-id="element-1046" data-mp="1cg5488vqmu80">
      <section className="border-b border-ink/15 bg-paper px-5 pb-20 pt-32 md:px-10 md:pb-24 md:pt-40" aria-labelledby="platform-heading" data-id="element-1047" data-mp="1cfvmt9ordvdx">
        <div className="mx-auto max-w-shell" data-id="element-1048" data-mp="2m733mwyezwez">
          <motion.p initial={reduceMotion ? false : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: easeEnter
        }} className="text-xs font-extrabold tracking-[0.16em] text-ink/55" data-id="element-1049" data-mp="2r4boj5ay6ejx">
            THE PLATFORM
          </motion.p>

          <motion.h1 id="platform-heading" initial={reduceMotion ? false : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: easeEnter,
          delay: stagger(1, reduceMotion)
        }} className="mt-6 max-w-5xl font-display text-[clamp(3rem,7.5vw,6.5rem)] font-bold leading-[0.86]" data-id="element-1050" data-mp="3etw4rgfpuxpi">
            The distribution and measurement layer for product experience.
          </motion.h1>

          <motion.div initial={reduceMotion ? false : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: easeEnter,
          delay: stagger(2, reduceMotion)
        }} className="mt-10 grid gap-10 md:grid-cols-[1fr_0.8fr] md:items-end" data-id="element-1051" data-mp="1mdd5tdpfogzr">
            <p className="max-w-xl text-lg leading-relaxed text-ink/70 md:text-xl" data-id="element-1052" data-mp="2k1wnamcn1tib">
              Every Sampler product runs on the same infrastructure: one place to
              plan a product moment, connect it to a real redemption, and follow
              what happened afterwards.
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-1053" data-mp="32ow4gy41olje">
              {products.map(product => <Link key={product.slug} to={product.path} className="inline-flex items-center gap-2.5 border border-ink/20 px-4 py-2.5 text-sm font-extrabold transition-colors hover:bg-ink hover:text-paper" data-id="element-1054" data-mp="2opedqd76uy7e">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${product.theme.dot}`} aria-hidden="true" data-id="element-1055" data-mp="2jrmjvdmzh20k" />
                  {product.shortName}
                </Link>)}
            </div>
          </motion.div>

          <Link to="/#start" className="mt-12 inline-flex min-h-14 items-center gap-2 rounded-full bg-signal px-7 text-sm font-extrabold text-white transition-colors hover:bg-signal-dark active:bg-signal-darker" data-id="element-1056" data-mp="1w0yypqo24fw2">
            START A CAMPAIGN
            <ArrowRightIcon size={17} aria-hidden="true" data-id="element-1057" data-mp="2zcegrge5sedz" />
          </Link>
        </div>
      </section>

      <Problem data-id="element-1058" data-mp="2ul62o71vw4uh" />
      <HowItWorks data-id="element-1059" data-mp="2uknzsrkpusdl" />
      <Features data-id="element-1060" data-mp="2uk4qfz94tcb6" />
      <Dashboard data-id="element-1061" data-mp="2ujld6vuffc3h" />
      <Testimonials data-id="element-1062" data-mp="2uj36f5a51fhb" />
      <CTA data-id="element-1063" data-mp="2pmuvk42z48fy" />
    </PageShell>;
}